import { useState, useEffect, type ReactNode } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useForensicsRequests,
    useForensicsRequest,
    useVaultForensicsEnvironments,
    useRunningForensicsEnvironments,
    useCreateForensicsRequest,
    useJoinForensicsEnvironment,
    useForensicsTools,
} from '../hooks'
import type { ForensicsMode, ForensicsRequest, ForensicsConnectToken } from '../api'
import { forensicsApi } from '../api'
import { ForensicsToolsDialog } from './ForensicsToolsDialog'
import { useToast } from '@/hooks/use-toast'
import {
    Microscope, Loader2, ExternalLink, Server, HardDrive, Network, Users,
    AlertTriangle, CheckCircle2, Clock, Wrench,
} from 'lucide-react'

interface StartForensicsDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectId: string
    objectVersionId?: number | null
    objectDescription?: string
    vaultId: number | null
    /** For a GROUP request (from a multi-select of objects). Defaults to [objectId]. */
    objectIds?: string[]
    /**
     * The object VERSION to investigate, e.g. 'AD1_AD_Full_20260720105327'.
     * Forensics builds ONE version, not the whole object: an object id is a prefix that
     * matches every version of it (AD1 has 121 Full versions — 2.4 TB, versus 9.49 GB for
     * one). The backend's ForensicsRequests.ObjectIDs holds version names for that reason.
     */
    objectVersionName?: string | null
}

// Mode → UI label. NEVER show the raw mode value; "security group" is never surfaced either.
const MODE_LABELS: Record<ForensicsMode, string> = {
    disk: 'Disk analysis',
    single: 'Isolated environment',
    multi: 'Multi-node forensics environment',
}

// Request status lifecycle → ordered progress steps + friendly labels.
// EVERY status the forensics account writes, in the order it writes them. 'Provisioning',
// 'Importing' and 'Announcing' were missing, and they are precisely the ones an environment
// spends the LONGEST in — a disk import alone runs for half an hour. indexOf returned -1 for
// all three, so no step was done and none was current: the analyst watched a row of empty grey
// circles with no spinner for thirty-five minutes, with nothing to say it was still working.
const STATUS_STEPS = [
    'Pending', 'SGCreated', 'CopyRequested', 'Copying',
    'Provisioning', 'Importing', 'Announcing', 'Ready',
]
const STATUS_LABELS: Record<string, string> = {
    Pending: 'Requested',
    SGCreated: 'Environment created',
    CopyRequested: 'Preparing data',
    Copying: 'Copying data',
    Provisioning: 'Building the environment',
    // Named for what the analyst is waiting for, not for the AWS operation: this is the long one.
    Importing: 'Preparing the machines to boot',
    Announcing: 'Starting the workstation',
    Ready: 'Ready',
    Expired: 'Expired',
    Error: 'Error',
}

/**
 * The workstation link.
 *
 * Matches DCV first and AppStream second. The link type changed to "Forensic Workstation (DCV)"
 * when the desktop moved off AppStream, and this predicate still said /appstream/i — so it found
 * nothing and the Connect button never appeared for any workstation actually built.
 */
const workstationLink = (r?: ForensicsRequest | null) =>
    r?.connectivityLinks?.find((l) => /dcv|workstation/i.test(l.type || '') && l.url)
    || r?.connectivityLinks?.find((l) => /appstream/i.test(l.type || '') && l.url)
    || null
const appStreamUrl = (r?: ForensicsRequest | null): string | null => workstationLink(r)?.url || null
const instanceLinks = (r?: ForensicsRequest | null) =>
    (r?.connectivityLinks || []).filter((l) => /instance/i.test(l.type || ''))

function ExpiryCountdown({ expiry }: { expiry?: string | null }) {
    if (!expiry) return null
    const t = new Date(expiry).getTime()
    if (Number.isNaN(t)) return null
    const mins = Math.max(0, Math.round((t - Date.now()) / 60000))
    return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" /> expires in {mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`}
        </span>
    )
}

function StatusBadge({ status }: { status: string }) {
    const variant = status === 'Ready' ? 'success' : status === 'Error' ? 'danger' : status === 'Expired' ? 'secondary' : 'info'
    return <Badge variant={variant as never}>{STATUS_LABELS[status] || status}</Badge>
}

function OptionCard({ active, onClick, icon, title, desc }: { active: boolean; onClick: () => void; icon: ReactNode; title: string; desc: string }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-lg border p-3 transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
        >
            <div className="flex items-center gap-2 text-sm font-medium">{icon}{title}</div>
            <p className="text-xs text-muted-foreground mt-1">{desc}</p>
        </button>
    )
}

function ProgressView({ request, loading }: { request?: ForensicsRequest; loading: boolean }) {
    if (loading && !request) {
        return <div className="py-8 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
    }
    if (!request) return <div className="py-8 text-center text-sm text-muted-foreground">No request data.</div>

    const status = request.status
    const stepIdx = STATUS_STEPS.indexOf(status)
    const instances = instanceLinks(request)

    return (
        <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Badge variant="secondary">{request.mode ? MODE_LABELS[request.mode] : 'Forensics'}</Badge>
                    <StatusBadge status={status} />
                </div>
                <ExpiryCountdown expiry={request.expiryTime} />
            </div>

            {status === 'Error' ? (
                <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:border-red-800/60 dark:bg-red-950/30">
                    <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    The forensics environment could not be provisioned. Please try again or contact support.
                </div>
            ) : status === 'Expired' ? (
                <div className="flex items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900/40">
                    <Clock className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    This forensics environment has expired and been torn down.
                </div>
            ) : (
                <ol className="space-y-1.5">
                    {STATUS_STEPS.map((s, i) => {
                        const done = stepIdx >= 0 && i < stepIdx
                        const current = i === stepIdx
                        return (
                            <li key={s} className="flex items-center gap-2 text-sm">
                                {done ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    : current ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                        : <span className="h-4 w-4 rounded-full border border-muted-foreground/30 inline-block" />}
                                <span className={current ? 'font-medium' : done ? 'text-muted-foreground' : 'text-muted-foreground/60'}>{STATUS_LABELS[s]}</span>
                            </li>
                        )
                    })}
                </ol>
            )}

            {status === 'Ready' && instances.length > 0 && (
                <Card><CardContent className="p-3 space-y-1">
                    <Label className="text-xs text-muted-foreground">Nodes</Label>
                    {instances.map((n, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                            <span className="font-mono">{n.instance_id || 'instance'}</span>
                            <span className="font-mono text-muted-foreground">{n.private_ip || ''}</span>
                        </div>
                    ))}
                </CardContent></Card>
            )}
        </div>
    )
}

export function StartForensicsDialog({ open, onOpenChange, objectId, objectDescription, vaultId, objectIds, objectVersionName }: StartForensicsDialogProps) {
    // Send VERSION names. Falls back to the object id only when no version is supplied, which
    // the backend can no longer resolve — so the caller should always pass one.
    const objectList = objectIds && objectIds.length > 0
        ? objectIds
        : (objectVersionName ? [objectVersionName] : [objectId])

    type Step = 'checking' | 'vault' | 'existing' | 'config' | 'progress'
    const [step, setStep] = useState<Step>('checking')
    const [envType, setEnvType] = useState<'isolated' | 'multi'>('isolated')
    const [multiAction, setMultiAction] = useState<'create' | 'join'>('create')
    const [analysis, setAnalysis] = useState<'disk' | 'boot'>('disk')
    const [joinEnvId, setJoinEnvId] = useState<number | null>(null)
    const [trackedId, setTrackedId] = useState<number | null>(null)
    const [selectedToolIds, setSelectedToolIds] = useState<number[]>([])
    const [toolsDialogOpen, setToolsDialogOpen] = useState(false)

    const { data: activeRequests, isLoading: checking } = useForensicsRequests(open ? objectId : null)
    // The vault's OWN environments — the first question the dialog answers. Scoped by vault
    // because an object's versions can live in different vaults, so an environment listed for
    // another vault could never hold the selected version.
    const vaultEnvs = useVaultForensicsEnvironments(open ? vaultId : null, open)
    const vaultEnvList = Array.isArray(vaultEnvs.data) ? vaultEnvs.data : []

    const running = useRunningForensicsEnvironments(open && envType === 'multi' && multiAction === 'join' ? vaultId : null, open)
    const createReq = useCreateForensicsRequest()
    const joinEnv = useJoinForensicsEnvironment()
    const tracked = useForensicsRequest(trackedId, step === 'progress')
    const toolsQ = useForensicsTools(open && step === 'config' ? vaultId : null)
    const toolList = Array.isArray(toolsQ.data) ? toolsQ.data : []

    const activeList: ForensicsRequest[] = Array.isArray(activeRequests) ? activeRequests : []
    const existing = activeList[0] || null
    const runningList = Array.isArray(running.data) ? running.data : []

    // Decide the initial step once the active-session check resolves.
    useEffect(() => {
        if (!open || checking) return
        // Open on the VAULT's environments, always. The old behaviour asked "does this object
        // have a session?" and jumped straight to the build form when it did not — which hid
        // every other environment in the vault the analyst could have used.
        if (step === 'checking' && !vaultEnvs.isLoading) setStep('vault')
    }, [open, checking, existing, step, vaultEnvs.isLoading])

    // Reset everything when the dialog closes.
    useEffect(() => {
        if (!open) {
            setStep('checking'); setEnvType('isolated'); setMultiAction('create')
            setAnalysis('disk'); setJoinEnvId(null); setTrackedId(null); setSelectedToolIds([])
        }
    }, [open])

    // A GROUP request (>1 object) is inherently a multi-node environment; default to it.
    useEffect(() => {
        if (open) setEnvType(objectList.length > 1 ? 'multi' : 'isolated')
    }, [open, objectList.length])

    const mode: ForensicsMode = envType === 'multi' ? 'multi' : (analysis === 'disk' ? 'disk' : 'single')
    const canCreate = vaultId != null && !(envType === 'multi' && multiAction === 'join')
    const canJoin = envType === 'multi' && multiAction === 'join' && joinEnvId != null

    const submitNew = async () => {
        if (envType === 'multi' && multiAction === 'join') {
            if (!joinEnvId) return
            await joinEnv.mutateAsync({ objectIds: objectList, joinEnvironmentId: joinEnvId, vaultId: vaultId ?? undefined })
            onOpenChange(false)
            return
        }
        if (vaultId == null) return
        const resp = await createReq.mutateAsync({ objectIds: objectList, mode, vaultId, toolIds: selectedToolIds })
        const created = (resp as { data?: { data?: ForensicsRequest } | ForensicsRequest }).data
        const newId = (created && 'data' in created ? created.data : created)?.id
        if (newId) { setTrackedId(Number(newId)); setStep('progress') }
    }

    /**
     * Open a workstation desktop.
     *
     * DCV authenticates against this application, so a token is minted HERE, at the click, and
     * spent immediately — it lives 60 seconds. There is no password anywhere in this flow by
     * design; if the mint is refused the user is told, rather than being sent to a login they
     * cannot pass.
     */
    const { toast } = useToast()
    const [connecting, setConnecting] = useState(false)
    const connect = async (r: ForensicsRequest) => {
        const link = workstationLink(r)
        if (!link?.url) { setTrackedId(r.id); setStep('progress'); return }
        setConnecting(true)
        try {
            const res = await forensicsApi.connectToken(r.id)
            const data = (res.data as { data?: ForensicsConnectToken })?.data ?? (res.data as unknown as ForensicsConnectToken)
            const session = data.sessionId || link.session_id || 'console'
            const url = `${data.url}?authToken=${encodeURIComponent(data.token)}#${encodeURIComponent(session)}`
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch (e) {
            const msg = (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
                ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error
                ?? 'Could not open the forensics desktop.'
            toast({ title: 'Connection refused', description: String(msg), variant: 'destructive' })
        } finally {
            setConnecting(false)
        }
    }

    const openExisting = (r: ForensicsRequest) => {
        if (r.status === 'Ready' && workstationLink(r)) { void connect(r); return }
        setTrackedId(r.id); setStep('progress')
    }

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Microscope className="h-5 w-5" /> Forensics</DialogTitle>
                    <DialogDescription>
                        Investigate {objectList.length > 1 ? `${objectList.length} objects` : <span className="font-mono">{objectId}</span>}
                        {objectDescription && objectList.length === 1 ? ` — ${objectDescription}` : ''} in an isolated forensics environment.
                    </DialogDescription>
                </DialogHeader>

                {step === 'checking' && (
                    <div className="py-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Checking for an existing session…
                    </div>
                )}

                {step === 'vault' && (
                    <div className="space-y-4 py-2">
                        <div className="flex items-baseline justify-between">
                            <Label className="text-sm font-medium">
                                Forensics environments in this vault
                            </Label>
                            <span className="text-xs text-muted-foreground font-mono">{vaultId ?? '—'}</span>
                        </div>

                        {vaultEnvs.isLoading && (
                            <div className="py-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Looking for existing environments…
                            </div>
                        )}

                        {!vaultEnvs.isLoading && vaultEnvList.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                No forensics environment is running in this vault.
                            </p>
                        )}

                        {vaultEnvList.map((env) => (
                            <Card key={env.requestId}><CardContent className="p-4 space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Badge variant="secondary">
                                            {env.mode ? (MODE_LABELS[env.mode as ForensicsMode] ?? env.mode) : 'Forensics'}
                                        </Badge>
                                        <StatusBadge status={env.status} />
                                    </div>
                                    <ExpiryCountdown expiry={env.expiryTime} />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {env.contents.length === 0
                                        ? 'No disks recorded'
                                        : env.contents.map((c) => c.objectId || c.versionName).join(', ')}
                                    {env.instanceCount > 0 && ` · ${env.instanceCount} machine${env.instanceCount > 1 ? 's' : ''}`}
                                </p>
                                <div className="flex flex-col gap-2 pt-1">
                                    <Button
                                        size="sm"
                                        disabled={!env.openable || connecting}
                                        onClick={() => openExisting({ id: env.requestId, status: env.status,
                                            connectivityLinks: env.workstationUrl
                                                ? [{ type: 'Forensic Workstation (DCV)', url: env.workstationUrl }]
                                                : [] } as unknown as ForensicsRequest)}
                                    >
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        {env.openable ? 'Open this environment' : 'Not ready to open'}
                                    </Button>
                                    {/* Deliberately shown-and-disabled rather than hidden: the analyst should
                                        learn that adding to an environment is a thing, and why it is not
                                        available, instead of wondering whether they missed it. */}
                                    <Button size="sm" variant="outline" disabled title={env.joinBlockedReason}>
                                        Add this disk to it — not available yet
                                    </Button>
                                    <p className="text-[11px] text-muted-foreground">{env.joinBlockedReason}</p>
                                </div>
                            </CardContent></Card>
                        ))}

                        <Button className="w-full" onClick={() => setStep('config')}>
                            Build a new environment
                        </Button>
                    </div>
                )}

                {step === 'existing' && existing && (
                    <div className="space-y-4 py-2">
                        <Card><CardContent className="p-4 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Badge variant="secondary">{existing.mode ? MODE_LABELS[existing.mode] : 'Forensics'}</Badge>
                                    <StatusBadge status={existing.status} />
                                </div>
                                <ExpiryCountdown expiry={existing.expiryTime} />
                            </div>
                            <p className="text-xs text-muted-foreground">An active forensics session already exists for this object.</p>
                        </CardContent></Card>
                        <div className="flex flex-col gap-2">
                            <Button onClick={() => openExisting(existing)}>
                                {existing.status === 'Ready' && appStreamUrl(existing)
                                    ? (<><ExternalLink className="h-4 w-4 mr-2" /> Open existing session</>)
                                    : 'View progress'}
                            </Button>
                            <Button variant="outline" onClick={() => setStep('config')}>Start a new session</Button>
                        </div>
                    </div>
                )}

                {step === 'config' && (
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-sm font-medium">Environment</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1.5">
                                <OptionCard active={envType === 'isolated'} onClick={() => setEnvType('isolated')} icon={<Server className="h-4 w-4" />} title="Isolated environment" desc="This object runs on its own." />
                                <OptionCard active={envType === 'multi'} onClick={() => setEnvType('multi')} icon={<Network className="h-4 w-4" />} title="Multi-node environment" desc="Group machines so they can communicate." />
                            </div>
                        </div>

                        {envType === 'multi' && (
                            <div>
                                <Label className="text-sm font-medium">Multi-node</Label>
                                <div className="grid grid-cols-2 gap-2 mt-1.5">
                                    <OptionCard active={multiAction === 'create'} onClick={() => setMultiAction('create')} icon={<Network className="h-4 w-4" />} title="Create new" desc="Start a new multi-node environment." />
                                    <OptionCard active={multiAction === 'join'} onClick={() => setMultiAction('join')} icon={<Users className="h-4 w-4" />} title="Join existing" desc="Add this object to a running one." />
                                </div>
                                {multiAction === 'join' && (
                                    <div className="mt-2">
                                        {running.isLoading ? (
                                            <p className="text-sm text-muted-foreground">Loading running environments…</p>
                                        ) : runningList.length === 0 ? (
                                            <p className="text-sm text-muted-foreground">No running multi-node environments to join.</p>
                                        ) : (
                                            <Select value={joinEnvId != null ? String(joinEnvId) : undefined} onValueChange={(v) => setJoinEnvId(Number(v))}>
                                                <SelectTrigger><SelectValue placeholder="Select an environment to join" /></SelectTrigger>
                                                <SelectContent>
                                                    {runningList.map((e) => (
                                                        <SelectItem key={e.environmentId} value={String(e.environmentId)}>
                                                            Environment #{e.environmentId} · {e.objectIds.length} node(s)
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {envType === 'isolated' && (
                            <div>
                                <Label className="text-sm font-medium">Analysis type</Label>
                                <div className="grid grid-cols-2 gap-2 mt-1.5">
                                    <OptionCard active={analysis === 'disk'} onClick={() => setAnalysis('disk')} icon={<HardDrive className="h-4 w-4" />} title="Disk analysis" desc="Mount the disk(s) read-only. Nothing boots." />
                                    <OptionCard active={analysis === 'boot'} onClick={() => setAnalysis('boot')} icon={<Server className="h-4 w-4" />} title="Boot as VM" desc="Boot the object as an isolated VM." />
                                </div>
                            </div>
                        )}

                        {!(envType === 'multi' && multiAction === 'join') && (
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm font-medium">Tools (optional)</Label>
                                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setToolsDialogOpen(true)}>
                                        <Wrench className="h-3.5 w-3.5" /> Manage
                                    </Button>
                                </div>
                                {toolsQ.isLoading ? (
                                    <p className="text-xs text-muted-foreground mt-1">Loading tools…</p>
                                ) : toolList.length === 0 ? (
                                    <p className="text-xs text-muted-foreground mt-1">No custom tools. Use "Manage" to add YARA/Sigma rules, scripts, or packages.</p>
                                ) : (
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {toolList.map((tool) => {
                                            const on = selectedToolIds.includes(tool.id)
                                            return (
                                                <button
                                                    key={tool.id}
                                                    type="button"
                                                    onClick={() => setSelectedToolIds((prev) => on ? prev.filter((x) => x !== tool.id) : [...prev, tool.id])}
                                                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/50'}`}
                                                >
                                                    {tool.name}
                                                </button>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {vaultId == null && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3 flex-shrink-0" /> No vault is associated with this object; a new environment can't be requested.
                            </p>
                        )}
                    </div>
                )}

                {step === 'progress' && <ProgressView request={tracked.data} loading={tracked.isLoading} />}

                <DialogFooter>
                    {step === 'config' ? (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button
                                onClick={submitNew}
                                disabled={(!canCreate && !canJoin) || createReq.isPending || joinEnv.isPending}
                            >
                                {(createReq.isPending || joinEnv.isPending) ? 'Submitting…'
                                    : (envType === 'multi' && multiAction === 'join') ? 'Join environment' : 'Request environment'}
                            </Button>
                        </>
                    ) : step === 'progress' ? (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                            {tracked.data?.status === 'Ready' && appStreamUrl(tracked.data) && (
                                <Button disabled={connecting} onClick={() => void connect(tracked.data!)}>
                                    {connecting
                                        ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting…</>)
                                        : (<><ExternalLink className="h-4 w-4 mr-2" /> Connect</>)}
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <ForensicsToolsDialog open={toolsDialogOpen} onOpenChange={setToolsDialogOpen} vaultId={vaultId} />
        </>
    )
}
